import { Alert, Button, Form, Input, Typography } from "antd";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../app/api.ts";
import { requestFailure, type RequestFailure } from "../app/form-errors.ts";
import { rateLimitedMessage } from "../app/retry-after.ts";
import { AuthCard } from "../components/AuthCard.tsx";

interface ForgotForm {
  email: string;
}

/**
 * Step 1 of the password reset. The API answers 202 whether or not the address has an account, and the screen
 * shows the same neutral confirmation either way, so neither reveals which addresses are registered.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();

  const mutation = useMutation<void, RequestFailure, ForgotForm>({
    mutationFn: async (values) => {
      const { error, response } = await api.POST("/auth/forgot-password", { body: { email: values.email.trim() } });
      if (!response.ok) throw requestFailure(error, response.status, response.headers);
    },
  });
  const errorMessage =
    mutation.error?.status === 429 ? rateLimitedMessage(t, mutation.error.retryAfterSeconds) : t("app.unavailable");

  return (
    <AuthCard subtitle={t("auth.forgot.title")}>
      {mutation.isSuccess ? (
        <Alert type="success" showIcon title={t("auth.forgot.sent")} role="status" />
      ) : (
        <>
          <Typography.Paragraph type="secondary">{t("auth.forgot.intro")}</Typography.Paragraph>
          {mutation.isError && <Alert type="error" showIcon title={errorMessage} style={{ marginBottom: 16 }} role="alert" />}
          <Form<ForgotForm> layout="vertical" requiredMark={false} onFinish={(values) => mutation.mutate(values)}>
            <Form.Item
              name="email"
              label={t("auth.forgot.email")}
              rules={[
                { required: true, whitespace: true, message: t("auth.required") },
                { type: "email", message: t("auth.forgot.emailInvalid") },
                { max: 254, message: t("auth.forgot.emailInvalid") },
              ]}
            >
              <Input type="email" autoComplete="email" autoFocus size="large" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={mutation.isPending}>
              {t("auth.forgot.submit")}
            </Button>
          </Form>
        </>
      )}
      <div style={{ textAlign: "center", marginTop: 16 }}>
        <Link to="/login">{t("auth.backToLogin")}</Link>
      </div>
    </AuthCard>
  );
}
